// frontend/src/CoachAttendance.js
import { useCallback, useEffect, useState } from "react";
import { FaCheckCircle, FaQuestionCircle, FaExclamationTriangle } from "react-icons/fa";
import { apiFetch } from "./api";
import { ymd, formatPracticeTimeRange } from "./utils";
import PracticeModal from "./PracticeModal";

export default function CoachAttendance({ coach, activeTab }) {
  const [upcomingDays, setUpcomingDays] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [upcomingError, setUpcomingError] = useState("");

  const [chipModal, setChipModal] = useState(null);
  const [practiceDetails, setPracticeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [mode, setMode] = useState("month");
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [weekDays, setWeekDays] = useState([]);
  const [weekPracticesByDay, setWeekPracticesByDay] = useState({});

  const [monthCursor, setMonthCursor] = useState(firstOfMonth(new Date()));
  const [monthDays, setMonthDays] = useState([]);
  const [monthPracticesByDay, setMonthPracticesByDay] = useState({});

  const [calError, setCalError] = useState("");
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const coachTeam = typeof coach?.team === "string" ? coach.team.trim() : "";


  const [unansweredThisWeek, setUnansweredThisWeek] = useState(0);
  const plural = (n, s) => (n === 1 ? s : `${s}s`);

  const loadUpcomingWeek = useCallback(async () => {
    if (!coach?.id) return;

    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    const from = ymd(start);
    const to = ymd(end);

    setLoadingUpcoming(true);
    setUpcomingError("");
    try {
      const [attendanceRows, practiceRange] = await Promise.all([
        apiFetch(`/attendance/coach/range?user=${coach.id}&from=${from}&to=${to}`),
        fetchPracticeRange(from, to, coachTeam),
      ]);

      // Build a map of practice_id -> status
      const statusLookup = new Map(
        (Array.isArray(attendanceRows) ? attendanceRows : []).map((row) => [
          Number(row.practice_id),
          row.status || null
        ])
      );

      const days = [];
      let missingCount = 0;
      for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
        const iso = ymd(cursor);
        const dayPractices = practiceRange.byDay[iso] || [];

        // Attach status to each practice individually
        const practicesWithStatus = [];
        for (const practice of dayPractices) {
          const practiceStatus = statusLookup.get(Number(practice.id)) || null;
          if (!practiceStatus) {
            missingCount += 1;
          }
          practicesWithStatus.push({
            ...practice,
            coachStatus: practiceStatus
          });
        }

        days.push({
          date: iso,
          status: null, // No longer used - status is per practice
          practices: practicesWithStatus,
        });
      }

      setUpcomingDays(days);
      setUnansweredThisWeek(missingCount);
    } catch (err) {
      console.error("loadUpcomingWeek error", err);
      setUpcomingError(err?.message || "Failed to load upcoming practices");
      setUpcomingDays([]);
      setUnansweredThisWeek(0);
    } finally {
      setLoadingUpcoming(false);
    }
  }, [coach?.id, coachTeam]);

  const loadWeek = useCallback(async () => {
    if (!coach?.id) return;
    const { from, to } = currentWeekRange(weekStart);
    setLoadingCalendar(true);
    setCalError("");
    try {
      const [attendanceRows, practiceRange] = await Promise.all([
        apiFetch(`/attendance/coach/range?user=${coach.id}&from=${from}&to=${to}`),
        fetchPracticeRange(from, to, coachTeam),
      ]);

      // Build a map of practice_id -> status
      const statusLookup = new Map(
        (Array.isArray(attendanceRows) ? attendanceRows : []).map((row) => [
          Number(row.practice_id),
          row.status || null
        ])
      );

      // Attach status to each practice
      const practicesByDayWithStatus = {};
      Object.keys(practiceRange.byDay).forEach(day => {
        practicesByDayWithStatus[day] = practiceRange.byDay[day].map(practice => ({
          ...practice,
          coachStatus: statusLookup.get(Number(practice.id)) || null
        }));
      });

      const days = [];
      for (let d = new Date(from); d <= new Date(to); d.setDate(d.getDate() + 1)) {
        const iso = ymd(d);
        days.push({ date: iso, status: null }); // status no longer used at day level
      }

      setWeekDays(days);
      setWeekPracticesByDay(practicesByDayWithStatus);
      // no day-level state
    } catch (err) {
      console.error("loadWeek error", err);
      setCalError(err?.message || "Failed to load week");
      setWeekDays([]);
      setWeekPracticesByDay({});
    } finally {
      setLoadingCalendar(false);
    }
  }, [coach?.id, coachTeam, weekStart]);

  const loadMonth = useCallback(async () => {
    if (!coach?.id) return;
    const { from, to } = monthRange(monthCursor);
    setLoadingCalendar(true);
    setCalError("");
    try {
      const [attendanceRows, practiceRange] = await Promise.all([
        apiFetch(`/attendance/coach/range?user=${coach.id}&from=${from}&to=${to}`),
        fetchPracticeRange(from, to, coachTeam),
      ]);

      // Build a map of practice_id -> status
      const statusLookup = new Map(
        (Array.isArray(attendanceRows) ? attendanceRows : []).map((row) => [
          Number(row.practice_id),
          row.status || null
        ])
      );

      // Attach status to each practice
      const practicesByDayWithStatus = {};
      Object.keys(practiceRange.byDay).forEach(day => {
        practicesByDayWithStatus[day] = practiceRange.byDay[day].map(practice => ({
          ...practice,
          coachStatus: statusLookup.get(Number(practice.id)) || null
        }));
      });

      setMonthDays(buildMonthGrid([], monthCursor));
      setMonthPracticesByDay(practicesByDayWithStatus);
      // no day-level state
    } catch (err) {
      console.error("loadMonth error", err);
      setCalError(err?.message || "Failed to load month");
      setMonthDays(buildMonthGrid([], monthCursor));
      setMonthPracticesByDay({});
    } finally {
      setLoadingCalendar(false);
    }
  }, [coach?.id, coachTeam, monthCursor]);

  const reloadCalendar = useCallback(async () => {
    if (mode === "month") {
      await loadMonth();
    } else {
      await loadWeek();
    }
  }, [mode, loadMonth, loadWeek]);

  const sendRSVP = useCallback(
    async (newStatus, practiceId) => {
      if (!coach?.id) return;
      if (!practiceId) return;

      try {
        await apiFetch(`/attendance/coach/mine?as=${coach.id}`, {
          method: "POST",
          body: JSON.stringify({ status: newStatus, practice_id: practiceId }),
        });
      } catch (err) {
        console.error("sendRSVP error", err);
        if (activeTab === "coachattendance-calendar") await reloadCalendar();
        return;
      }

      if (activeTab === "coachattendance-calendar") await reloadCalendar();
      await loadUpcomingWeek();
    },
    [coach?.id, activeTab, reloadCalendar, loadUpcomingWeek]
  );

  useEffect(() => {
    if (activeTab === "coachattendance-calendar") {
      reloadCalendar();
    }
  }, [activeTab, reloadCalendar]);

  useEffect(() => {
    if (activeTab === "coachattendance") {
      loadUpcomingWeek();
    }
  }, [activeTab, loadUpcomingWeek]);

  const loadPracticeDetails = useCallback(async (practiceId) => {
    if (!practiceId) return;
    setPracticeDetails(null);
    setLoadingDetails(true);
    try {
      const practiceData = await apiFetch(`/practices/${practiceId}`);
      const practiceDate = practiceData.date?.slice(0, 10);

      let coachesWithStatus = [];
      try {
        const coachAttendance = await apiFetch(`/attendance/coach/by-date?date=${practiceDate}&practice=${practiceId}`);
        coachesWithStatus = (coachAttendance || []).map((c) => ({
          name: c.coachName,
          userId: c.id,
          status: c.status || null,
        }));
      } catch (e) {
        console.warn("Could not fetch coach attendance:", e);
      }

      let workouts = [];
      try {
        const workoutList = await apiFetch(`/practices/${practiceId}/workouts`);
        workouts = Array.isArray(workoutList) ? workoutList : [];
      } catch (e) {
        console.warn("Could not fetch workouts:", e);
      }

      let lineupStatuses = [];
      try {
        const boatsData = await apiFetch(`/boats/by-practice/${practiceId}`);
        const boats = Array.isArray(boatsData?.boats) ? boatsData.boats : [];
        const teamSet = [...new Set(boats.map((b) => b.team).filter(Boolean))];

        let publishedByTeam = {};
        if (practiceDate && teamSet.length > 0) {
          try {
            const pubData = await apiFetch(`/lineups/${practiceDate}/published-squads`);
            (pubData?.items || []).forEach((item) => {
              if (!publishedByTeam[item.team]) publishedByTeam[item.team] = [];
              publishedByTeam[item.team].push(item);
            });
          } catch (e) {
            console.warn("Could not fetch published squads:", e);
          }
        }

        lineupStatuses = teamSet.map((team) => {
          const pubEntries = publishedByTeam[team] || [];
          const isPublished = pubEntries.length > 0;
          return {
            team,
            published: isPublished,
            publishedAt: isPublished ? pubEntries[0].publishedAt : null,
            publishedBy: isPublished ? pubEntries[0].publishedBy : null,
          };
        });
      } catch (e) {
        console.warn("Could not fetch lineup statuses:", e);
      }

      setChipModal((prev) => prev ? { ...prev, practice: { ...prev.practice, ...practiceData } } : prev);
      setPracticeDetails({ coaches: coachesWithStatus, practiceInfo: practiceData, workouts, lineupStatuses });
    } catch (e) {
      console.error("Failed to load practice details:", e);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  function openPracticeModal(practice, statusForDay) {
    setPracticeDetails(null);
    setChipModal({ practice, status: statusForDay || null });
    loadPracticeDetails(practice.id);
  }

  function closePracticeModal() {
    setChipModal(null);
    setPracticeDetails(null);
  }

  function CalendarToolbar() {
    const title =
      mode === "month"
        ? monthLabel(monthCursor)
        : (() => {
            const { from, to } = currentWeekRange(weekStart);
            return `${from} – ${to}`;
          })();

    return (
      <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
        <h2 className="h2" style={{ margin: 0 }}>My Availability Calendar</h2>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <select
            className="input selectInput"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <option value="month">Month</option>
            <option value="week">Week</option>
          </select>
          <div className="dateNav">
            <button
              className="btn ghost small pill"
              onClick={mode === "month" ? () => setMonthCursor(firstOfMonth(addMonths(monthCursor, -1))) : () => setWeekStart(startOfWeek(addDays(weekStart, -7)))}
            >
              ←
            </button>
            <strong>{title}</strong>
            <button
              className="btn ghost small pill"
              onClick={mode === "month" ? () => setMonthCursor(firstOfMonth(addMonths(monthCursor, 1))) : () => setWeekStart(startOfWeek(addDays(weekStart, 7)))}
            >
              →
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderPracticeModal() {
    if (!chipModal?.practice) return null;
    const day = chipModal.practice.date?.slice(0, 10) || todayStr();
    const modalTitle = new Date(`${day}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "long", month: "short", day: "numeric",
    });
    return (
      <PracticeModal
        isOpen={true}
        onClose={closePracticeModal}
        practice={chipModal.practice}
        coach={coach}
        loadingDetails={loadingDetails}
        practiceDetails={practiceDetails}
        showRsvpButtons={true}
        rsvpStatus={chipModal.status}
        onRsvp={async (newStatus) => {
          await sendRSVP(newStatus, chipModal.practice.id);
          setChipModal((prev) => (prev ? { ...prev, status: newStatus } : prev));
        }}
        enableResponseFields={false}
        modalTitle={modalTitle}
      />
    );
  }

  if (activeTab === "coachattendance-calendar") {
    return (
      <div className="stack">
        <div className="card">
          <CalendarToolbar />
          {calError && (
            <p className="help" style={{ color: "red", marginTop: 8 }}>
              Error: {calError}
            </p>
          )}
          {loadingCalendar && (
            <p className="help" style={{ marginTop: 8 }}>Loading…</p>
          )}

          {mode === "week" && (
            <>
              <p className="help" style={{ marginTop: 8 }}>
                Tap a practice to mark your availability. Use the arrows to change weeks.
              </p>
              <ul className="list" style={{ marginTop: 8 }}>
                {weekDays.map(({ date: day }) => {
                  const chips = weekPracticesByDay[day] || [];
                  return (
                    <li key={day} className="item" style={{ cursor: "default" }}>
                      <div className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <strong>{prettyDay(day)}</strong>
                      </div>
                      <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                        {chips.map((practice) => (
                          <button
                            key={`${practice.id || "practice"}-${practice.date}-${practice.startTime || "all-day"}-${practice.endTime || ""}`}
                            className="practice-chip big"
                            title={practice.note || ""}
                            onClick={() => openPracticeModal(practice, practice.coachStatus)}
                          >
                            {attendanceStatusIcon(practice.coachStatus)}
                            <span className="practiceChipLabel">{formatPracticeChipText(practice)}</span>
                          </button>
                        ))}
                        {chips.length === 0 && <span className="help" style={{ opacity: 0.6 }}>No practices</span>}
                      </div>
                    </li>
                  );
                })}
                {weekDays.length === 0 && !loadingCalendar && (
                  <li className="item">
                    <span className="help">No days in range.</span>
                  </li>
                )}
              </ul>
            </>
          )}

          {mode === "month" && (
            <>
              <div className="weekdayHeader">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
                  <div key={label}>{label}</div>
                ))}
              </div>
              <div className="monthGrid">
                {monthDays.map(({ date: day, outside }) => {
                  const chips = monthPracticesByDay[day] || [];
                  const isToday = day === todayStr();
                  const dayDate = new Date(`${day}T00:00:00`);
                  return (
                    <div
                      key={day}
                      className={`dayCell ${outside ? "outside" : ""} ${isToday ? "todayCell" : ""}`}
                    >
                      <div className="calendarDayHeader">
                        <div className="calendarDayWeekday">{dayDate.toLocaleDateString(undefined, { weekday: "short" })}</div>
                        <div className={`dayNum ${isToday ? "today" : ""}`}>
                          {dayDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </div>
                      </div>
                      <div className="calendarPracticeList" style={{ gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                        {chips.map((practice) => (
                          <button
                            key={`${practice.id || "practice"}-${practice.date}-${practice.startTime || "all-day"}-${practice.endTime || ""}`}
                            className="practice-chip"
                            title={practice.note || ""}
                            onClick={() => openPracticeModal(practice, practice.coachStatus)}
                          >
                            {attendanceStatusIcon(practice.coachStatus)}
                            <span className="practiceChipLabel">{formatPracticeChipText(practice)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="row" style={{ marginTop: 10 }}>
                <button className="btn ghost" onClick={() => setMonthCursor(firstOfMonth(new Date()))}>
                  This month
                </button>
              </div>
            </>
          )}
        </div>
        {renderPracticeModal()}
      </div>
    );
  }

  return (
    <div className="stack">

        {unansweredThisWeek > 0 && (
          <div
            className="alertRow"
            role="alert"
            aria-live="polite"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              marginBottom: 12,
              borderRadius: 8,
              background: "rgba(255, 193, 7, 0.15)",
              border: "1px solid rgba(255, 193, 7, 0.5)"
            }}>
          <FaExclamationTriangle aria-hidden="true" />
        <div style={{ fontWeight: 600 }}>
          You have {unansweredThisWeek} {plural(unansweredThisWeek, "practice")} this week that you have not responded to.
        </div>
        </div>
)}
      <div className="card">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h3 className="h2" style={{ margin: 0 }}>Upcoming Week</h3>
          <span className="help">Next 7 days</span>
        </div>
        {upcomingError && (
          <p className="help" style={{ color: "red", marginTop: 8 }}>Error: {upcomingError}</p>
        )}
        {loadingUpcoming ? (
          <p className="help" style={{ marginTop: 12 }}>Loading upcoming practices…</p>
        ) : upcomingDays.length > 0 ? (
          <div className="upcomingGrid" style={{ marginTop: 12 }}>
            {upcomingDays.map(({ date: day, status: dayStatus, practices }) => {
              const prettyLabel = prettyDay(day);

              return (
                <div key={day} className="upcomingDay">
                  <div className="upcomingHeader">
                    <div className="upcomingDate">
                      <strong>{prettyLabel}</strong>
                    </div>
                  </div>

                  <div className="upcomingPractices">
                    {practices.length > 0 ? (
                      practices.map((practice) => {
                        const practiceStatus = practice.coachStatus;

                        return (
                          <div
                            key={`${practice.id || "practice"}-${practice.date}-${practice.startTime || "all-day"}-${practice.endTime || ""}`}
                            className="upcomingPracticeCard"
                          >
                            <div className="upcomingPracticeHeader">
                              <button
                                className="upcomingPracticeDetail"
                                title={practice.note || ""}
                                type="button"
                                onClick={() => openPracticeModal(practice, practiceStatus)}
                              >
                                {formatPracticeChipText(practice)}
                              </button>
                              <span className="upcomingPracticeStatus">
                                {attendanceStatusIcon(practiceStatus)}
                              </span>
                            </div>
                            <div className="upcomingPracticeActions">
                              <button
                                className={`btn attendanceBtn present ${practiceStatus === "present" ? "selected" : ""}`}
                                onClick={() => sendRSVP("present", practice.id)}
                              >
                                I'll be there
                              </button>
                              <button
                                className={`btn attendanceBtn absent ${practiceStatus === "absent" ? "selected" : ""}`}
                                onClick={() => sendRSVP("absent", practice.id)}
                              >
                                Can't attend
                              </button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <span className="help" style={{ opacity: 0.7 }}>No practices scheduled.</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="help" style={{ marginTop: 12 }}>No practices scheduled this week.</p>
        )}
      </div>
      {renderPracticeModal()}
    </div>
  );
}

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfWeek(d) {
  const tmp = new Date(d);
  const day = tmp.getDay();
  tmp.setDate(tmp.getDate() - day);
  tmp.setHours(0, 0, 0, 0);
  return tmp;
}

function firstOfMonth(d) {
  const tmp = new Date(d);
  tmp.setDate(1);
  tmp.setHours(0, 0, 0, 0);
  return tmp;
}

function addMonths(d, delta) {
  const tmp = new Date(d);
  tmp.setMonth(tmp.getMonth() + delta);
  return tmp;
}

function addDays(d, delta) {
  const tmp = new Date(d);
  tmp.setDate(tmp.getDate() + delta);
  return tmp;
}

function monthLabel(d) {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function currentWeekRange(startDate) {
  const start = new Date(startDate);
  const end = new Date(startDate);
  end.setDate(end.getDate() + 6);
  return { from: ymd(start), to: ymd(end) };
}

function monthRange(d) {
  const first = firstOfMonth(d);
  const last = addMonths(first, 1);
  last.setDate(0);
  return { from: ymd(first), to: ymd(last) };
}

function buildMonthGrid(monthData, cursor) {
  const first = firstOfMonth(cursor);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());

  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = ymd(d);
    const inSameMonth = d.getMonth() === cursor.getMonth();
    const found = monthData.find((row) => row.date === iso);
    cells.push({ date: iso, status: found?.status || null, outside: !inSameMonth });
  }
  return cells;
}

function prettyDay(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function attendanceStatusIcon(status) {
  const value = typeof status === "string" ? status : null;
  if (value === "Available") {
    return <FaCheckCircle className="statusIcon present" aria-label="Available" />;
  }
  if (value === "Not Available") {
    return (
      <span className="statusIcon absent custom-absent" style={{ width: 16, height: 16 }} aria-label="Not Available">
        <span className="absent-icon-inner">×</span>
      </span>
    );
  }
  return <FaQuestionCircle className="statusIcon pending" aria-label="No Response" />;
}

function formatPracticeChipText(practice) {
  const timeLabel = formatPracticeTimeRange(practice);
  const squadLabel = practice.squad ? String(practice.squad).toUpperCase() : "ALL";
  return `${timeLabel} | ${squadLabel}`;
}



async function fetchPracticeRange(from, to, team) {
  const params = new URLSearchParams({ from, to });
  if (team) params.append("team", team);
  const rows = await apiFetch(`/practices/range?${params.toString()}`);
  const byDay = {};

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const day = String(row.date || "").slice(0, 10);
    if (!day) return;

    const startRaw = row.startTime ?? row.start_time ?? row.time ?? null;
    const endRaw = row.endTime ?? row.end_time ?? null;
    const normalized = {
      id: row.id,
      date: day,
      startTime: startRaw ? String(startRaw).slice(0, 5) : null,
      endTime: endRaw ? String(endRaw).slice(0, 5) : null,
      time: startRaw ? String(startRaw).slice(0, 5) : null,
      note: row.note || "",
      squad: row.squad || row.squadName || null,
      squadIds: Array.isArray(row.squadIds) ? row.squadIds : (row.squadId ? [row.squadId] : []),
      team: row.team || null,
    };

    if (!byDay[day]) byDay[day] = [];
    if (normalized.startTime) {
      byDay[day].push(normalized);
    }
  });

  Object.keys(byDay).forEach((day) => {
    byDay[day].sort((a, b) => (a.startTime || "99:99").localeCompare(b.startTime || "99:99"));
  });

  return { byDay };
}
